import type {
  PodEpisodeGetPayload,
  PodEpisodeSelect,
  PodcastGetPayload,
  PodcastSelect,
  ResourceLinkSelect,
} from '@erikdakoda/database/types';
import {
  ResTaxonomyForGridSelect,
  ResAuthorForGridSelect,
  ResourceLinkForGridSelect,
} from '@erikdakoda/taxonomy/types';
import { z } from 'zod';

export const CleanupTranscriptSchema = z.object({
  formattedTranscript: z.string(),
  compressedTranscript: z.string(),
});

export type CleanupTranscriptType = z.infer<typeof CleanupTranscriptSchema>;

export const PodcastForGridSelect = {
  id: true,
  title: true,
  subtitle: true,
  description: true,
  summary: true,
  score: true,
  scoreCount: true,
  domain: true,
  coverImageId: true,
  categories: true,
  rssFeedUrl: true,
  explicit: true,
  podcastType: true,
  ownerName: true,
  complete: true,
  lastEpisodePubDate: true,
  createdAt: true,
  updatedAt: true,
} satisfies PodcastSelect;

export type PodcastForGrid = PodcastGetPayload<{
  select: typeof PodcastForGridSelect;
}>;

export const PodcastForDetailSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  status: true,
  publishedAt: true,
  version: true,
  indexedAt: true,
  processingAt: true,
  warnings: true,
  slug: true,
  title: true,
  subtitle: true,
  description: true,
  summary: true,
  domain: true,
  language: true,
  score: true,
  scoreCount: true,
  coverImageId: true,
  categories: true,
  websiteUrl: true,
  copyright: true,
  rssFeedUrl: true,
  managingEditorEmail: true,
  webMasterEmail: true,
  lastBuiltAt: true,
  docsUrl: true,
  software: true,
  ttlMinutes: true,
  explicit: true,
  ownerName: true,
  ownerEmail: true,
  podcastType: true,
  newFeedUrl: true,
  block: true,
  complete: true,
  lastEpisodePubDate: true,
  sources: true,
  tags: true,
  resTaxonomies: {
    select: ResTaxonomyForGridSelect,
  },
  resAuthors: {
    select: ResAuthorForGridSelect,
  },
  resourceLinks: {
    select: ResourceLinkForGridSelect,
  },
  _count: {
    select: {
      podEpisodes: true,
    },
  },
} satisfies PodcastSelect;

export type PodcastForDetail = PodcastGetPayload<{
  select: typeof PodcastForDetailSelect;
}>;

/**
 * Lean select for the admin PodEpisode data grid list rows.
 * Omits full-text description/summary and nested podcast/relation graphs;
 * those load via {@link PodEpisodeForDetailSelect} when a detail panel opens.
 */
export const PodEpisodeForGridSelect = {
  id: true,
  title: true,
  subtitle: true,
  domain: true,
  coverImageId: true,
  categories: true,
  status: true,
  publishedAt: true,
  publishedOn: true,
  indexedAt: true,
  warnings: true,
  pubDate: true,
  episode: true,
  season: true,
  episodeType: true,
  explicit: true,
  durationSeconds: true,
  createdAt: true,
  updatedAt: true,
  podcastId: true,
  podcast: {
    select: {
      id: true,
      title: true,
      subtitle: true,
      coverImageId: true,
    },
  },
  _count: {
    select: {
      transcripts: true,
      resTaxonomies: true,
    },
  },
} satisfies PodEpisodeSelect;

export type PodEpisodeForGrid = PodEpisodeGetPayload<{
  select: typeof PodEpisodeForGridSelect;
}>;

export const PodEpisodeForDetailSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  status: true,
  publishedAt: true,
  version: true,
  indexedAt: true,
  processingAt: true,
  warnings: true,
  slug: true,
  title: true,
  subtitle: true,
  description: true,
  summary: true,
  domain: true,
  language: true,
  score: true,
  scoreCount: true,
  coverImageId: true,
  categories: true,
  publishedOn: true,
  websiteUrl: true,
  copyright: true,
  guid: true,
  pubDate: true,
  episodeUrl: true,
  enclosureUrl: true,
  enclosureLength: true,
  enclosureType: true,
  durationSeconds: true,
  episode: true,
  season: true,
  episodeType: true,
  explicit: true,
  itunesBlock: true,
  podcastId: true,
  podcast: { select: PodcastForDetailSelect },
  sources: true,
  tags: true,
  resTaxonomies: {
    select: ResTaxonomyForGridSelect,
  },
  resAuthors: {
    select: ResAuthorForGridSelect,
  },
  resourceLinks: {
    select: ResourceLinkForGridSelect,
  },
  _count: {
    select: {
      transcripts: true,
    },
  },
} satisfies PodEpisodeSelect;

export type PodEpisodeForDetail = PodEpisodeGetPayload<{
  select: typeof PodEpisodeForDetailSelect;
}>;

export const ResourceLinkForEnrichSelect = {
  id: true,
  platform: true,
  format: true,
  externalId: true,
} satisfies ResourceLinkSelect;

export const PodcastForEnrichSelect = {
  id: true,
  title: true,
  resourceLinks: { select: ResourceLinkForEnrichSelect },
} satisfies PodcastSelect;

export const PodEpisodeForEnrichSelect = {
  id: true,
  title: true,
  guid: true,
  previewUrl: true,
  durationSeconds: true,
  warnings: true,
  podcast: { select: PodcastForEnrichSelect },
} satisfies PodEpisodeSelect;

export type PodEpisodeForEnrich = PodEpisodeGetPayload<{
  select: typeof PodEpisodeForEnrichSelect;
}>;
